import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Message, Confirm } from 'semantic-ui-react';

import { graphql } from 'react-apollo';
import { toNumber } from 'lodash';
import withPagination from './WithPagination';
import Search from '../views/GroupManager/Search';
import List from '../views/GroupManager/List';
import customerDealerGroupQuery from '../graphql/customerDealerGroupQuery';
import { setSearch } from '../data/searchField/actions';
import { setPage as setPageAction } from '../data/page/actions';
import createDealerGroupMutation from '../graphql/createDealerGroupMutation';
import updateDealerGroupMutation from '../graphql/updateDealerGroupMutation';
import LoadingDataEnhancer from './LoadingData';
import GroupManagerForm from '../views/GroupManager/Form';
import deleteDealerGroupMutation from '../graphql/deleteDealerGroupMutation';
import ImportDealer from '../views/GroupManager/ImportDealer';

const defaultDealerGroup = {
  groupId: 0,
  groupName: '',
  groupDescription: '',
};

const messageSuccess = {
  Create: 'Successfully Created New Dealer Group',
  Update: 'Successfully Updated Dealer Group',
  Delete: 'Successfully Deleted Dealer Group',
};

const GroupAction = {
  Create: 'Create',
  Update: 'Update',
  Delete: 'Delete',
  Import: 'Import',
};

class GroupManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: '',
      selectedDealerGroup: null,
      isModalOpen: false,
      formError: null,
      formSubmitting: null,
      flashMessage: null,
      isDeleteConfirmationOpen: false,
      isNegativeFlashMsg: false,
      isBulkImportDisplay: false,
    };
  }

  groupNameSearchQuery = event => {
    const { groupName } = event.target;
    const { data, setSearchFields, setPage } = this.props;
    if (groupName.value.length > 100) {
      this.setState({
        error: 'Group name is too long (maximum 100 characters)',
      });
      return false;
    }
    const variableArguments = {
      groupName: groupName.value,
    };
    data.refetch({
      ...variableArguments,
    });

    this.setState({ error: '' });
    setSearchFields(variableArguments);
    setPage({});
    return true;
  };

  onSelectDealerGroup = (action, dealerGroup) => {
    this.setState({
      selectedDealerGroup: dealerGroup,
      flashMessage: null,
    });

    switch (action) {
      case 'Edit':
        this.setState({
          isModalOpen: true,
        });
        break;
      case GroupAction.Delete:
        this.setState({
          isDeleteConfirmationOpen: true,
        });
        break;
      case GroupAction.Import:
        this.setState({
          isBulkImportDisplay: true,
        });
        break;
      default:
        this.setState({
          selectedDealerGroup: null,
        });
    }
  };

  onFormSubmitHandler = async values => {
    const {
      createDealerGroupMutation: createMutation,
      updateDealerGroupMutation: updateMutation,
      deleteDealerGroupMutation: deleteMutation,
    } = this.props;
    const { selectedDealerGroup } = this.state;
    this.setState({
      formSubmitting: true,
    });
    const { action } = values;
    try {
      switch (action) {
        case GroupAction.Create:
          await this.onExcuteMutationDealerGroup(
            createMutation,
            'createCustomerGroup',
            values,
            action
          );
          break;
        case GroupAction.Update:
          await this.onExcuteMutationDealerGroup(
            updateMutation,
            'updateCustomerGroup',
            values,
            action
          );
          break;
        case GroupAction.Delete:
          await this.onExcuteMutationDealerGroup(
            deleteMutation,
            'deleteCustomerGroup',
            selectedDealerGroup,
            action
          );
          break;
        default:
          this.setState({
            formSubmitting: false,
          });
      }
    } catch (error) {
      this.onFailedSubmitDealerGroup(
        Array.isArray(error) ? error : error.message,
        action
      );
    }
  };

  onFormCloseHandler = () => {
    this.setState({
      isModalOpen: false,
      formError: null,
      formSubmitting: null,
    });
  };

  onClickNewDealerGroup = () => {
    this.setState({
      selectedDealerGroup: defaultDealerGroup,
      isModalOpen: true,
    });
  };

  successSubmitDealerGroup = action => {
    this.setState({
      formSubmitting: false,
      formError: null,
      flashMessage: messageSuccess[action],
      isNegativeFlashMsg: false,
      isDeleteConfirmationOpen: false,
    });
  };

  onFailedSubmitDealerGroup = (errorMessage, action) => {
    if (action === GroupAction.Delete) {
      this.setState({
        flashMessage: errorMessage,
        isDeleteConfirmationOpen: false,
        isNegativeFlashMsg: true,
      });
    } else {
      this.setState({
        formSubmitting: false,
        formError: errorMessage,
      });
    }
  };

  getDealerGroupMutationActionInput = values => {
    // Using a property access on 'this' to satisfy class-methods-use-this rule
    this.processedGroupId = toNumber(values.groupId);
    return {
      groupId: this.processedGroupId,
      groupName: values.groupName,
      groupDescription: values.groupDescription,
    };
  };

  onExcuteMutationDealerGroup = async(
    mutation,
    mutationResultName,
    values,
    action
  ) => {
    const { setPage, data } = this.props;
    const input = this.getDealerGroupMutationActionInput(values);
    await mutation({
      variables: { ...input },
    })
      .then(({ data: responseData }) => {
        const { success, msg } = responseData[mutationResultName];

        if (success) {
          this.successSubmitDealerGroup(action);
          this.onFormCloseHandler();
          setPage({});
          data.refetch();
        } else {
          this.onFailedSubmitDealerGroup(msg, action);
        }
      })
      .catch(error => {
        this.onFailedSubmitDealerGroup(
          Array.isArray(error) ? error : error.message,
          action
        );
      });
  };

  onCancelDelete = () => {
    this.setState({
      isDeleteConfirmationOpen: false,
    });
  };

  onCloseBulkImport = () => {
    this.setState({ isBulkImportDisplay: false });
  };

  render() {
    const {
      data: { customerDealerGroupList },
      searchFields,
    } = this.props;

    const {
      error,
      selectedDealerGroup,
      formSubmitting,
      isModalOpen,
      formError,
      flashMessage,
      isNegativeFlashMsg,
      isBulkImportDisplay,
      isDeleteConfirmationOpen,
    } = this.state;

    return (
      <div>
        {/* Success message */}
        {flashMessage && (
          <Message
            onDismiss={this.handleMessageDismiss}
            content={flashMessage}
            positive
            negative={isNegativeFlashMsg}
          />
        )}
        <b>Group Manager</b>
        <br />
        <br />
        <Button color='green' onClick={this.onClickNewDealerGroup} inverted>
          Create Dealer Group
        </Button>
        <br />
        <br />
        <Search
          onSubmit={this.groupNameSearchQuery}
          searchFields={searchFields}
          searchOptions={['groupName']}
        />
        {error && <span style={{ color: 'red' }}>{error}</span>}
        <br />
        <br />
        <List
          onSelectDealerGroup={this.onSelectDealerGroup}
          customerDealerGroupList={customerDealerGroupList}
        />
        {isModalOpen && (
          <GroupManagerForm
            isModalOpen={isModalOpen}
            onCloseHandler={this.onFormCloseHandler}
            onSubmitHandler={this.onFormSubmitHandler}
            selectedDealerGroup={selectedDealerGroup}
            error={formError}
            submitting={formSubmitting}
          />
        )}
        <Confirm
          open={isDeleteConfirmationOpen}
          onCancel={this.onCancelDelete}
          onConfirm={() => this.onFormSubmitHandler({ action: GroupAction.Delete })}
        />
        <ImportDealer
          group={selectedDealerGroup}
          isModalOpen={isBulkImportDisplay}
          onCloseHandler={this.onCloseBulkImport}
        />
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPageAction());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
  };
};

GroupManager.propTypes = {
  data: PropTypes.shape({
    refetch: PropTypes.func.isRequired,
    customerDealerGroupList: PropTypes.array,
  }),
  searchFields: PropTypes.object,
  setSearchFields: PropTypes.func,
  setPage: PropTypes.func,
  createDealerGroupMutation: PropTypes.func,
  updateDealerGroupMutation: PropTypes.func,
  deleteDealerGroupMutation: PropTypes.func,
};

export default compose(
  withRouter,
  withPagination(customerDealerGroupQuery, 'customerDealerGroupList'),
  graphql(createDealerGroupMutation, { name: 'createDealerGroupMutation' }),
  graphql(updateDealerGroupMutation, { name: 'updateDealerGroupMutation' }),
  graphql(deleteDealerGroupMutation, { name: 'deleteDealerGroupMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(GroupManager);
